import { Button, Loader, RichTextEditor } from "@/components/ui";
import { Paragraph } from "@/components/ui/Typography";
import { useState } from "react";
import { useMicroSiteContext } from "@/contexts/MicroSiteContext";
import Input from '@/components/ui/Input/Input';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import FileUpload from '@/components/ui/FileUpload';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import UploadIcon from "@/assets/icons/UploadIcon";
import { showToast } from '@/lib/toast';
import { TemplateDataSchemaType } from "@/pages/articles-pages/programmes/MultiStepForm/MicroSite/Templates/templateDataSchema";
import Calendar from "@/components/ui/Calendar/Calendar";
import formatDate from "@/utils/helpers/formatDate";


const FormDataSchema = z.object({
  missionTimeLineSectionTitle: z.string().min(1, 'Title is required'),
  missionTimeLineSectionDescription: z.string().min(1, 'Details are required'),
  missionTimeLineSectionImageUrl: z.string().optional(),
  missionTimeLineStartDate: z.string(),
  missionTimeLineEndDate: z.string(),
});

const TimelineItemForm = ({setShowForm, setIsEditing, timelineData}:{setShowForm?: (value: boolean) => void; setIsEditing?: (value: boolean) => void; timelineData?: TemplateDataSchemaType["missionTimeLineSection"]["missionTimeLineSectionDetails"][0]}) => {
    const [isUploading, setIsUploading] = useState(false);
    const { templatePayload:templateData, updateTemplatePayload } = useMicroSiteContext();

    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
      const fileUploadMutation = useFileUpload();
  
  const {
          control,
          handleSubmit,
          setValue,
          formState: { errors },
          reset
        } = useForm({
          resolver: zodResolver(FormDataSchema),
          defaultValues: {
            missionTimeLineSectionTitle: timelineData?.missionTimeLineSectionTitle ?? '',
          missionTimeLineSectionDescription: timelineData?.missionTimeLineSectionDescription ?? '',
          missionTimeLineSectionImageUrl: timelineData?.missionTimeLineSectionImageUrl ?? '',
          missionTimeLineStartDate: timelineData?.missionTimeLineStartDate ?? '',
          missionTimeLineEndDate: timelineData?.missionTimeLineEndDate ?? '',
          },
          mode: 'all'
        });
    
      const handleSave = (formData: TemplateDataSchemaType["missionTimeLineSection"]["missionTimeLineSectionDetails"][0]) => {
        setShowForm?.(false);
        setIsEditing?.(false);
        const updatedPayload = {
          ...templateData,
          missionTimeLineSection: {
            ...templateData.missionTimeLineSection,
            missionTimeLineSectionDetails: timelineData ? templateData.missionTimeLineSection.missionTimeLineSectionDetails.map((item) =>
            item === timelineData ? { ...item, missionTimeLineSectionTitle: formData.missionTimeLineSectionTitle, missionTimeLineSectionDescription: formData.missionTimeLineSectionDescription, missionTimeLineSectionImageUrl: formData.missionTimeLineSectionImageUrl, 
            missionTimeLineStartDate: formData.missionTimeLineStartDate, 
            missionTimeLineEndDate: formData.missionTimeLineEndDate } : item
          ) : templateData.missionTimeLineSection.missionTimeLineSectionDetails.concat([formData])
          }
        };
        updateTemplatePayload(updatedPayload);
      };
  
          const handleFileChange = async(files: File[] | null) => {
          if (files && files.length > 0) {
            setIsUploading(true);
            const file = files[0];
            try {
              const response = await fileUploadMutation.mutateAsync({
                file: file
              });
              setValue('missionTimeLineSectionImageUrl', response?.data?.fileUrl, { shouldValidate: true });
              setPreviewUrl(response?.data?.fileUrl);
            } catch (error) {
              console.error('File upload failed:', error);
              showToast.error('File upload failed. Please try again.');
            } finally {
              setIsUploading(false);
            }
          } else {
            setPreviewUrl(null);
            setValue('missionTimeLineSectionImageUrl', "", { shouldValidate: true });
          }
        };

    return (
      <div className="flex flex-col gap-4 w-full p-4 lg:p-8 bg-[#F2F2F2]">
        <form onSubmit={handleSubmit(handleSave)} className="flex flex-col gap-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
          name="missionTimeLineStartDate"
          control={control}
          render={({ field }) => (
            <Calendar
            {...field}
              placeholder="Start Date"
              label="Start Date"
              error={errors.missionTimeLineStartDate?.message}
              $dataId="missionTimeLineStartDate-input"
              showMonthYearPicker
              $dateFormat="MMMM YYYY"
              onChange={(data)=>{
                setValue(field.name, formatDate(new Date(data),"MMMM YYYY"), {
                  shouldValidate: true
                })
              }}
            />
          )}
        />
                <Controller
          name="missionTimeLineEndDate"
          control={control}
          render={({ field }) => (
            <Calendar
            {...field}
              placeholder="End Date"
              label="End Date"
              error={errors.missionTimeLineEndDate?.message}
              $dataId="missionTimeLineEndDate-input"
              showMonthYearPicker
              $dateFormat="MMMM YYYY"
              onChange={(data)=>{
                setValue(field.name, formatDate(data,"MMMM YYYY"), {
                  shouldValidate: true
                })
              }}
            />
          )}
        />

          </div>
     
         <Controller
          name="missionTimeLineSectionTitle"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Title"
              label="Title"
              error={errors.missionTimeLineSectionTitle?.message}
              $dataId="missionTimeLineSectionTitle-input"
              required
              
            />
          )}
        />

           <Controller
          name="missionTimeLineSectionDescription"
          control={control}
          render={({ field }) => (
            <RichTextEditor
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Description"
              label="Description"
              error={errors.missionTimeLineSectionDescription?.message}
            />
          )}
        />

           <div className="max-h-64 overflow-hidden flex items-center justify-center">
          {
            (previewUrl || timelineData?.missionTimeLineSectionImageUrl) && !isUploading ? (
              <img src={previewUrl || timelineData?.missionTimeLineSectionImageUrl} alt="Preview" className="h-full w-full object-cover object-center rounded" />
            ) : null
          }
            
          </div>

          <div className="relative">
              {
              isUploading && <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                <Loader />  
              </div> 
            }
            <Controller
          name="missionTimeLineSectionImageUrl"
          control={control}
          render={({ field }) => (
            <FileUpload
            {...field}
              onChange={(files) => {
                handleFileChange(files);
                field.onChange(files ? URL.createObjectURL(files[0]) : '');
              }}
              label=""
              placeholder=""
              $maxSizeMB={5}
              error={errors.missionTimeLineSectionImageUrl?.message}
              multiple={false}
              allowedFileTypes={['image/*']}
              containerClassName="border border-[#E5E5E5] bg-white"
              $dataId="inline-file-upload"
            >
              <div className="flex flex-col items-center justify-center gap-4">
                <UploadIcon stroke="#00764B" />
                <Paragraph className="font-semibold text-primary">
                  Click or drag and drop file to upload
                </Paragraph>
                <Paragraph className="text-sm text-text-secondary">
                  Supported document file is PNG, & JPEG with max size of 5mb
                </Paragraph>
                <button
                  type="button"
                  className="bg-[#ECFEF8] text-[#00764B] hover:bg-[#ECFEF8] hover:text-[#000] px-4 py-2 rounded-md font-semibold"
                >
                  Browse file
                </button>
              </div>
            </FileUpload>
          )}
        />
          </div>

        
          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="secondary"
              className=""
              onClick={() => {
                setShowForm?.(false);
                setIsEditing?.(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button
              className=""
              onClick={handleSubmit(handleSave)}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    )
  };

  export default TimelineItemForm;